import { useState, useEffect, useCallback } from 'react';
import { fetchAllDeliveryBoysStats, type DeliveryBoyStats } from '@/lib/supabase';

type Period = 'today' | 'month' | 'all';

export default function DeliveryStatsPanel() {
  const [stats, setStats] = useState<DeliveryBoyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('today');
  const [selectedBoy, setSelectedBoy] = useState<DeliveryBoyStats | null>(null);

  const loadStats = useCallback(async (p: Period) => {
    setLoading(true);
    const data = await fetchAllDeliveryBoysStats(p);
    setStats(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(period); }, [period, loadStats]);

  const totalDelivered = stats.reduce((s, b) => s + b.totalDelivered, 0);
  const totalCash = stats.reduce((s, b) => s + b.cashTotal, 0);
  const totalOnline = stats.reduce((s, b) => s + b.onlineTotal, 0);
  const totalExchanges = stats.reduce((s, b) => s + b.sizeExchangeCount, 0);
  const totalReturns = stats.reduce((s, b) => s + b.returnCount, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
            <i className="ri-bar-chart-box-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Delivery Performance</p>
            <p className="text-gray-400 text-xs">Har delivery boy ki report</p>
          </div>
        </div>
        <button onClick={() => loadStats(period)} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all">
          <i className={`ri-refresh-line text-gray-600 ${loading ? 'animate-spin' : ''}`}></i>
        </button>
      </div>

      {/* Period Filter */}
      <div className="bg-white rounded-2xl border border-gray-100 p-1 flex gap-1">
        {(['today', 'month', 'all'] as Period[]).map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${period === p ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
            {p === 'today' ? 'Today' : p === 'month' ? '30 Days' : 'All Time'}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center mb-2">
            <i className="ri-check-double-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalDelivered}</p>
          <p className="text-gray-400 text-xs mt-0.5">Total Delivered</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center mb-2">
            <i className="ri-money-rupee-circle-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">₹{(totalCash + totalOnline).toLocaleString('en-IN')}</p>
          <p className="text-gray-400 text-xs mt-0.5">Total Collection</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="w-9 h-9 rounded-xl bg-orange-400 flex items-center justify-center mb-2">
            <i className="ri-t-shirt-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalExchanges}</p>
          <p className="text-gray-400 text-xs mt-0.5">Size Exchanges</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="w-9 h-9 rounded-xl bg-red-400 flex items-center justify-center mb-2">
            <i className="ri-arrow-go-back-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalReturns}</p>
          <p className="text-gray-400 text-xs mt-0.5">Returns</p>
        </div>
      </div>

      {/* Cash vs Online Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
              <i className="ri-money-rupee-circle-fill text-amber-600"></i>
            </div>
            <span className="text-amber-700 text-xs font-bold">Cash</span>
          </div>
          <p className="text-amber-900 font-bold text-xl">₹{totalCash.toLocaleString('en-IN')}</p>
          <p className="text-amber-600 text-xs mt-0.5">{stats.reduce((s, b) => s + b.cashCount, 0)} orders</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center">
              <i className="ri-qr-code-line text-green-600"></i>
            </div>
            <span className="text-green-700 text-xs font-bold">Online</span>
          </div>
          <p className="text-green-900 font-bold text-xl">₹{totalOnline.toLocaleString('en-IN')}</p>
          <p className="text-green-600 text-xs mt-0.5">{stats.reduce((s, b) => s + b.onlineCount, 0)} orders</p>
        </div>
      </div>

      {/* Per Boy Stats */}
      <div>
        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">Per Delivery Boy</p>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
            <p className="text-gray-400 text-sm">Load ho raha hai...</p>
          </div>
        ) : stats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-gray-100">
            <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
              <i className="ri-e-bike-2-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-sm">Is period mein koi delivery nahi hua</p>
          </div>
        ) : (
          <div className="space-y-3">
            {stats.map(boy => (
              <div key={boy.boyPhone} className="bg-white rounded-2xl border border-gray-100 overflow-hidden cursor-pointer hover:border-gray-200 transition-all"
                onClick={() => setSelectedBoy(selectedBoy?.boyPhone === boy.boyPhone ? null : boy)}>
                {/* Boy Header */}
                <div className="flex items-center gap-3 p-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white text-lg font-bold shrink-0">
                    {boy.boyName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-bold text-sm">{boy.boyName}</p>
                    <p className="text-gray-400 text-xs">{boy.boyPhone}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-gray-900 font-bold text-lg">{boy.totalDelivered}</p>
                    <p className="text-gray-400 text-xs">delivered</p>
                  </div>
                  <i className={`${selectedBoy?.boyPhone === boy.boyPhone ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 shrink-0`}></i>
                </div>

                {/* Quick Chips */}
                <div className="px-4 pb-3 flex flex-wrap gap-2">
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-xs font-semibold">
                    <i className="ri-money-rupee-circle-fill"></i>₹{boy.cashTotal.toLocaleString('en-IN')} cash
                  </span>
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 border border-green-100 text-green-700 text-xs font-semibold">
                    <i className="ri-qr-code-line"></i>₹{boy.onlineTotal.toLocaleString('en-IN')} online
                  </span>
                  {boy.sizeExchangeCount > 0 && (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-100 text-orange-600 text-xs font-semibold">
                      <i className="ri-swap-line"></i>{boy.sizeExchangeCount} exchange
                    </span>
                  )}
                  {boy.returnCount > 0 && (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 border border-red-100 text-red-600 text-xs font-semibold">
                      <i className="ri-arrow-go-back-line"></i>{boy.returnCount} return
                    </span>
                  )}
                </div>

                {/* Expanded Details */}
                {selectedBoy?.boyPhone === boy.boyPhone && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Detailed Breakdown</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-gray-400 text-xs">Cash Orders</p>
                        <p className="text-gray-900 font-bold text-lg">{boy.cashCount}</p>
                        <p className="text-amber-600 text-xs font-semibold">₹{boy.cashTotal.toLocaleString('en-IN')}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-gray-400 text-xs">Online Orders</p>
                        <p className="text-gray-900 font-bold text-lg">{boy.onlineCount}</p>
                        <p className="text-green-600 text-xs font-semibold">₹{boy.onlineTotal.toLocaleString('en-IN')}</p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-3">
                        <p className="text-orange-500 text-xs font-semibold">Size Exchanges</p>
                        <p className="text-gray-900 font-bold text-lg">{boy.sizeExchangeCount}</p>
                        <p className="text-orange-400 text-xs">Customer ne size badla</p>
                      </div>
                      <div className="bg-red-50 rounded-xl p-3">
                        <p className="text-red-500 text-xs font-semibold">Returns</p>
                        <p className="text-gray-900 font-bold text-lg">{boy.returnCount}</p>
                        <p className="text-red-400 text-xs">Wapas laye products</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-xs">Total Collection</p>
                        <p className="text-gray-900 font-bold text-xl">₹{(boy.cashTotal + boy.onlineTotal).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 text-xs">Success Rate</p>
                        <p className="text-green-600 font-bold text-lg">
                          {boy.totalDelivered > 0
                            ? Math.round(((boy.totalDelivered - boy.returnCount) / boy.totalDelivered) * 100)
                            : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
